import React, { memo, useMemo, useState } from "react";
import { Folder, FolderOpen, MoreVertical, Pencil, Trash2, TrendingUp, ChevronRight, UserCheck, DraftingCompass, Users as UsersCode } from "lucide-react";
import { cn } from "../../utils/cn";
import { Project, User } from "../../types";

/**
 * Status gradient configurations for the top accent bar
 */
const statusGradients: Record<string, string> = {
    active: "from-emerald-500 to-green-600",
    completed: "from-blue-500 to-indigo-600",
    archived: "from-slate-400 to-slate-500",
    planned: "from-sky-400 to-blue-500",
    "on-hold": "from-amber-500 to-orange-600",
};

const statusConfigs: Record<string, { color: string; label: string }> = {
    active: { color: "bg-emerald-500", label: "Active" },
    completed: { color: "bg-blue-500", label: "Completed" },
    archived: { color: "bg-slate-500", label: "Archived" },
    planned: { color: "bg-sky-500", label: "Planned" },
    "on-hold": { color: "bg-amber-500", label: "On Hold" },
};

const priorityConfigs: Record<string, { color: string; label: string; bgColor: string }> = {
    critical: { color: "text-red-600", bgColor: "bg-red-50 dark:bg-red-900/20", label: "Critical" },
    high: { color: "text-orange-600", bgColor: "bg-orange-50 dark:bg-orange-900/20", label: "High" },
    medium: { color: "text-blue-600", bgColor: "bg-blue-50 dark:bg-blue-900/20", label: "Medium" },
    low: { color: "text-slate-600", bgColor: "bg-slate-100 dark:bg-slate-800", label: "Low" },
};

/**
 * PM Avatar component
 */
const PMAvatar = memo(function PMAvatar({ user, size = 'sm' }: { user: User; size?: 'sm' | 'md' }) {
    const sizeClasses = {
        sm: 'w-7 h-7 text-[10px]',
        md: 'w-8 h-8 text-xs'
    };

    const initials = user.displayName
        ?.split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase() || user.email?.[0]?.toUpperCase() || '?';

    return (
        <div
            className={cn(
                sizeClasses[size],
                "rounded-full flex items-center justify-center font-bold ring-2 ring-white dark:ring-slate-900 relative group/avatar cursor-help",
                user.photoUrl ? "" : "bg-gradient-to-br from-siemens-petrol to-siemens-deep-blue text-white"
            )}
            title={user.displayName || user.email}
        >
            {user.photoUrl ? (
                <img src={user.photoUrl} alt={user.displayName} className="w-full h-full rounded-full object-cover" />
            ) : (
                initials
            )}
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[10px] font-medium rounded opacity-0 group-hover/avatar:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {user.displayName || user.email}
            </div>
        </div>
    );
});

/**
 * PM Avatar group for stacked display
 */
const PMGroup = memo(function PMGroup({ users, maxDisplay = 3 }: { users: User[]; maxDisplay?: number }) {
    const displayUsers = users.slice(0, maxDisplay);
    const remainingCount = users.length - maxDisplay;

    if (users.length === 0) {
        return (
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                    <span className="text-[9px] uppercase font-bold text-slate-400">TBD</span>
                </div>
                <span className="text-[10px] text-slate-400 font-medium">Unassigned</span>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
                {displayUsers.map((user) => (
                    <PMAvatar key={user.id} user={user} />
                ))}
                {remainingCount > 0 && (
                    <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center font-bold text-[10px] text-slate-600 dark:text-slate-300 ring-2 ring-white dark:ring-slate-900">
                        +{remainingCount}
                    </div>
                )}
            </div>
            <span className="text-[10px] text-slate-500 font-medium">
                {users.length} PM{users.length > 1 ? 's' : ''}
            </span>
        </div>
    );
});

interface ProjectCardProps {
    project: Project & { epicCount?: number; avgWsjf?: number };
    selected?: boolean;
    onClick?: () => void;
    onEdit?: (id: string, e: React.MouseEvent) => void;
    onDelete?: (id: string, e: React.MouseEvent) => void;
    showActions?: boolean;
    laneDistribution?: {
        now: number;
        next: number;
        later: number;
        unassigned: number;
        done: number;
    };
    users?: User[];
    roleCounts?: {
        pmCount: number;
        ownerCount: number;
        architectCount: number;
    };
}

export const ProjectCard = memo(function ProjectCard({
    project,
    selected,
    onClick,
    onEdit,
    onDelete,
    showActions = true,
    laneDistribution = { now: 0, next: 0, later: 0, unassigned: 0, done: 0 },
    users = [],
    roleCounts
}: ProjectCardProps) {
    const [showMenu, setShowMenu] = useState(false);

    const statusConfig = statusConfigs[project.status || 'planned'] || statusConfigs.planned;
    const priorityConfig = priorityConfigs[project.priority || 'medium'] || priorityConfigs.medium;
    const statusGradient = statusGradients[project.status || 'planned'] || statusGradients.planned;

    // Get assigned PMs
    const assignedPMs = useMemo(() => {
        if (!users || !project.pm) return [];
        const pmArray = Array.isArray(project.pm) ? project.pm : [project.pm];
        return pmArray
            .map(id => users.find(u => u.id === id))
            .filter(Boolean) as User[];
    }, [project.pm, users]);

    const handleMenuClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(false);
        onEdit?.(project.id, e);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(false);
        onDelete?.(project.id, e);
    };

    const totalEpics = laneDistribution.now + laneDistribution.next + laneDistribution.later + laneDistribution.unassigned;

    return (
        <div
            onClick={onClick}
            className={cn(
                "group relative bg-white dark:bg-slate-900/80 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300",
                "border border-black/5 dark:border-white/5",
                "hover:shadow-xl hover:shadow-black/10 dark:hover:shadow-black/30",
                "hover:-translate-y-1 hover:scale-[1.01]",
                selected && "ring-2 ring-siemens-petrol shadow-lg shadow-siemens-petrol/20"
            )}
        >
            {/* Status Gradient Accent Bar */}
            <div className={cn(
                "h-1.5 w-full bg-gradient-to-r",
                statusGradient
            )} />

            {/* Card Content */}
            <div className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={cn(
                            "w-12 h-12 rounded-xl flex items-center justify-center transition-colors",
                            "bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900",
                            selected && "from-siemens-petrol/10 to-siemens-petrol/5"
                        )}>
                            {selected ? (
                                <FolderOpen className="w-6 h-6 text-siemens-petrol" />
                            ) : (
                                <Folder className="w-6 h-6 text-siemens-petrol" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base font-black text-slate-900 dark:text-white truncate leading-tight">
                                {project.name}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                                <span className={cn(
                                    "text-[10px] font-bold uppercase px-2 py-0.5 rounded-full",
                                    statusConfig.color,
                                    "text-white"
                                )}>
                                    {statusConfig.label}
                                </span>
                                <span className={cn(
                                    "text-[10px] font-bold uppercase px-2 py-0.5 rounded-full",
                                    priorityConfig.bgColor,
                                    priorityConfig.color
                                )}>
                                    {priorityConfig.label}
                                </span>
                            </div>
                        </div>
                    </div>
                    {showActions && (
                        <div className="relative">
                            <button
                                onClick={handleMenuClick}
                                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 opacity-0 group-hover:opacity-100 transition-all"
                            >
                                <MoreVertical className="w-4 h-4 text-slate-400" />
                            </button>
                            {showMenu && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                                    <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1 min-w-[140px]">
                                        <button
                                            onClick={handleEdit}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                        >
                                            <Pencil className="w-4 h-4" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={handleDelete}
                                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Description */}
                {project.description && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 mb-4 leading-relaxed">
                        {project.description}
                    </p>
                )}

                {/* Stats Row */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-6">
                        {/* Epic Count */}
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-siemens-petrol/10 to-siemens-petrol/5 flex items-center justify-center">
                                <TrendingUp className="w-4 h-4 text-siemens-petrol" />
                            </div>
                            <div>
                                <span className="text-lg font-black text-slate-900 dark:text-white">
                                    {project.epicCount || totalEpics || 0}
                                </span>
                                <span className="text-[10px] text-slate-400 uppercase font-bold ml-1">epics</span>
                            </div>
                        </div>

                        {/* Avg WSJF */}
                        <div className="flex items-baseline gap-1">
                            <span className="text-lg font-black bg-gradient-to-r from-siemens-deep-blue to-siemens-petrol bg-clip-text text-transparent">
                                {(project.avgWsjf || 0).toFixed(1)}
                            </span>
                            <span className="text-[10px] text-slate-400 uppercase font-medium">avg WSJF</span>
                        </div>
                    </div>

                    {/* PM Avatars */}
                    <PMGroup users={assignedPMs} />
                </div>

                {/* Role Counts Row */}
                <div className="flex items-center gap-4 mb-4 px-1 py-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    {/* PM Count */}
                    <div className="flex items-center gap-1.5" title="Project Managers">
                        <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <UsersCode className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                            {assignedPMs.length}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">PM</span>
                    </div>

                    {/* Owner Count */}
                    <div className="flex items-center gap-1.5" title="Service Owners">
                        <div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                            <UserCheck className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                            {/* Placeholder or passed prop - logic below */}
                            {roleCounts?.ownerCount || 0}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">Own</span>
                    </div>

                    {/* Architect Count */}
                    <div className="flex items-center gap-1.5" title="Solution Architects">
                        <div className="w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <DraftingCompass className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                        </div>
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                            {roleCounts?.architectCount || 0}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">Arq</span>
                    </div>
                </div>

                {/* Progress Bar - Done percentage */}
                <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Progress</span>
                        <span className={cn(
                            "text-xs font-black",
                            laneDistribution.done >= 100 ? "text-emerald-500" :
                                laneDistribution.done >= 50 ? "text-siemens-petrol" : "text-slate-500"
                        )}>
                            {laneDistribution.done}%
                        </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                        <div
                            className={cn(
                                "h-full rounded-full transition-all duration-500",
                                laneDistribution.done >= 100
                                    ? "bg-gradient-to-r from-emerald-400 to-emerald-500"
                                    : laneDistribution.done >= 50
                                        ? "bg-gradient-to-r from-siemens-petrol to-siemens-petrol-dark"
                                        : "bg-gradient-to-r from-amber-400 to-amber-500"
                            )}
                            style={{ width: `${Math.min(laneDistribution.done, 100)}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Navigate Arrow */}
            <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1">
                <ChevronRight className="w-6 h-6 text-siemens-petrol" />
            </div>
        </div>
    );
});

export default ProjectCard;
